import type { FileRouteParams } from '@jotta/types/schemas/FileRouteSchema'
import { makeFileRouteCatchSchema } from '@jotta/types/schemas/FileRouteSchema'
import { safeDecodeURIComponent, uriDecodePath } from '@jotta/utils/pathUtils'
import { isString } from '@jotta/utils/typeAssertions'
import moize from 'moize'
import { mapValues } from 'radash'
import type { Params } from 'react-router-dom'
import { matchPath, useParams } from 'react-router-dom'
import { sortByCookie, viewModeCookie } from './cookies'

function decodeParams<T extends string>(
  params: Params<T> | undefined,
): typeof params {
  if (params) {
    return mapValues(params, (v, k) =>
      v ? (k === '*' ? uriDecodePath(v) : safeDecodeURIComponent(v)) : v,
    )
  }
  return undefined
}
function fileRoutePathToParams(path: string) {
  const searchMatch = matchPath(
    '/web/search/:viewMode/:sortBy/:query/*',
    path,
  )?.params
  if (searchMatch) {
    return {
      context: 'search',
      ...decodeParams(searchMatch),
    }
  }
  return (
    decodeParams(
      matchPath('/web/:context/:viewMode/:sortBy/*', path)?.params,
    ) ||
    decodeParams(matchPath('/web/:context/:viewMode', path)?.params) ||
    decodeParams(matchPath('/web/:context', path)?.params)
  )
}

/**
 * Parse value to valid FileRouteParams
 * @param value Value to parse
 */
export const getFileRouteParams = moize.shallow(function getFileRouteParams(
  value: unknown,
): FileRouteParams {
  const params = isString(value) ? fileRoutePathToParams(value) : value

  return makeFileRouteCatchSchema({
    viewMode: viewModeCookie.get(),
    sortBy: sortByCookie.get(),
  }).parse(params)
})

export function useFileRouteParams() {
  const params = useParams()

  return getFileRouteParams(params)
}
